// src/hooks/useVisiblePractitioners.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import type { UserUuid } from "../../types/identity";

export type CalendarPrefView = "day" | "week-all" | "list";

const VIEWS: CalendarPrefView[] = ["day", "week-all", "list"];

export const CALENDAR_PREFS_KEY = "mg.appointments.calendarPrefs.v1";

// Scoped per user so a shared clinic workstation does not hand one practitioner's layout to the next.
export const calendarPrefsKey = (userId?: UserUuid | null): string => `${CALENDAR_PREFS_KEY}.${userId ?? "anon"}`;

interface StoredPrefs {
	visibleIds: UserUuid[];
	view: CalendarPrefView;
}

const readPrefs = (key: string): StoredPrefs | null => {
	try {
		const raw = window.localStorage.getItem(key);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed?.visibleIds)) return null;
		// Integer ids stored before the uuid cutover are dropped here rather than pruned against the roster.
		const visibleIds = parsed.visibleIds.filter((id: unknown): id is UserUuid => typeof id === "string" && id !== "");
		// A stored "week" pref predates the removed week-of-one mode; it lands on the merged all-staff view.
		const view = parsed?.view === "week" ? "week-all" : VIEWS.includes(parsed?.view) ? parsed.view : "day";
		return { visibleIds, view };
	} catch {
		// A blocked or corrupt store must fall back to defaults, never break the calendar.
		return null;
	}
};

interface UseVisiblePractitionersOptions {
	teamMembers: { id: UserUuid }[];
	userId?: UserUuid | null;
}

export interface VisiblePractitioners {
	visibleIds: Set<UserUuid>;
	ready: boolean;
	allVisible: boolean;
	onlyMine: boolean;
	onlyMineUnavailable: boolean;
	view: CalendarPrefView;
	setView: (view: CalendarPrefView) => void;
	toggle: (id: UserUuid) => void;
	showAll: () => void;
	showNone: () => void;
	showOnlyMine: () => void;
}

export const useVisiblePractitioners = ({ teamMembers, userId }: UseVisiblePractitionersOptions): VisiblePractitioners => {
	const storageKey = calendarPrefsKey(userId);

	const [view, setView] = useState<CalendarPrefView>(() => readPrefs(calendarPrefsKey(userId))?.view ?? "day");
	const [visibleIds, setVisibleIds] = useState<Set<UserUuid>>(() => new Set());
	const [hydrated, setHydrated] = useState<boolean>(false);
	const [onlyMineUnavailable, setOnlyMineUnavailable] = useState<boolean>(false);

	const rosterIds = useMemo(() => teamMembers.map((member) => member.id), [teamMembers]);

	// The roster arrives async, so stored ids can only be reconciled against it once it has landed.
	useEffect(() => {
		if (rosterIds.length === 0) return;
		const roster = new Set(rosterIds);

		if (!hydrated) {
			const storedIds = readPrefs(storageKey)?.visibleIds ?? null;
			const kept = storedIds ? storedIds.filter((id) => roster.has(id)) : [];
			setVisibleIds(new Set(kept.length > 0 ? kept : rosterIds));
			setHydrated(true);
			return;
		}

		// A practitioner who has left is pruned; emptying a non-empty set that way falls back to all,
		// while a set the user deliberately emptied is left alone.
		setVisibleIds((current) => {
			const kept = [...current].filter((id) => roster.has(id));
			if (kept.length === current.size) return current;
			return new Set(kept.length > 0 ? kept : rosterIds);
		});
	}, [rosterIds, storageKey, hydrated]);

	useEffect(() => {
		if (!hydrated) return;
		try {
			window.localStorage.setItem(storageKey, JSON.stringify({ visibleIds: [...visibleIds], view }));
		} catch {
			// Prefs are best-effort; a full or blocked store must not break the calendar.
		}
	}, [hydrated, storageKey, visibleIds, view]);

	const toggle = useCallback((id: UserUuid) => {
		setOnlyMineUnavailable(false);
		setVisibleIds((current) => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const showAll = useCallback(() => {
		setOnlyMineUnavailable(false);
		setVisibleIds(new Set(rosterIds));
	}, [rosterIds]);

	const showNone = useCallback(() => {
		setOnlyMineUnavailable(false);
		setVisibleIds(new Set());
	}, []);

	// Reception and other non-clinical staff are not on the roster — showing them nothing would be a dead calendar.
	const showOnlyMine = useCallback(() => {
		const mine = userId != null && rosterIds.includes(userId);
		setOnlyMineUnavailable(!mine);
		setVisibleIds(mine ? new Set([userId]) : new Set(rosterIds));
	}, [rosterIds, userId]);

	// Everyone counts as visible until the stored set has landed, so nothing downstream flashes an empty calendar.
	const effectiveIds = useMemo(() => (hydrated ? visibleIds : new Set(rosterIds)), [hydrated, visibleIds, rosterIds]);

	const allVisible = rosterIds.length > 0 && rosterIds.every((id) => effectiveIds.has(id));
	const onlyMine = userId != null && effectiveIds.size === 1 && effectiveIds.has(userId);

	return { visibleIds: effectiveIds, ready: hydrated, allVisible, onlyMine, onlyMineUnavailable, view, setView, toggle, showAll, showNone, showOnlyMine };
};
